import React from "react";
import { Iconography, IconographySizes } from "@hid-galaxy-ui/galaxy-react";
import type {
  IntegrationMilestone,
  IntegrationMilestoneState,
} from "../../../pages/hid-admin/dashboard/partner-organizations/partner-list/integration-status/integrationStatusTypes";
import {
  getMilestoneMeta,
  getTimelineProgressPercent,
} from "./timelineUtils";
import "./IntegrationTimeline.scss";

const IntegrationTimelineNode: React.FC<{ state: IntegrationMilestoneState }> = ({
  state,
}) => {
  if (state === "completed") {
    return (
      <span className="integration-timeline__node" aria-hidden>
        <Iconography icon="circleCheck" size={IconographySizes.Small} />
      </span>
    );
  }

  if (state === "in-progress") {
    return (
      <span
        className="integration-timeline__node integration-timeline__node--in-progress"
        aria-hidden
      >
        <span className="integration-timeline__node-in-progress">
          <span className="integration-timeline__node-in-progress-halo" />
          <span className="integration-timeline__node-in-progress-inner" />
        </span>
      </span>
    );
  }

  if (state === "on-hold") {
    return (
      <span className="integration-timeline__node" aria-hidden>
        <Iconography icon="triangleExclamation" size={IconographySizes.Small} />
      </span>
    );
  }

  return <span className="integration-timeline__node" aria-hidden />;
};

export type IntegrationTimelineProps = {
  milestones: IntegrationMilestone[];
  className?: string;
  pendingLabel?: string;
  selectedLabel?: string;
  onStepSelect?: (label: string) => void;
  segmentedRail?: boolean;
};

const isSegmentConnectorActive = (milestone: IntegrationMilestone): boolean =>
  milestone.state !== "pending";

const IntegrationTimeline: React.FC<IntegrationTimelineProps> = ({
  milestones,
  className,
  pendingLabel,
  selectedLabel,
  onStepSelect,
  segmentedRail = false,
}) => {
  const stepCount = milestones.length;
  const progressPercent = getTimelineProgressPercent(milestones);

  const renderStep = (milestone: IntegrationMilestone) => {
    const isPending = milestone.state === "pending";
    const isSelected = selectedLabel === milestone.label;
    const isInteractive = Boolean(onStepSelect);

    const stepContent = (
      <>
        <div className="integration-timeline__track">
          <IntegrationTimelineNode state={milestone.state} />
        </div>
        <span className="integration-timeline__label">{milestone.label}</span>
        <span
          className={`integration-timeline__meta${
            isPending ? " integration-timeline__meta--pending" : ""
          }`}
        >
          {getMilestoneMeta(milestone, pendingLabel)}
        </span>
      </>
    );

    return isInteractive ? (
      <button
        type="button"
        className={`integration-timeline__step integration-timeline__step--${milestone.state} integration-timeline__step--selectable${
          isSelected ? " integration-timeline__step--selected" : ""
        }`}
        key={milestone.label}
        onClick={() => onStepSelect?.(milestone.label)}
        aria-pressed={isSelected}
        role="listitem"
      >
        {stepContent}
      </button>
    ) : (
      <div
        className={`integration-timeline__step integration-timeline__step--${milestone.state}`}
        key={milestone.label}
        role="listitem"
      >
        {stepContent}
      </div>
    );
  };

  const renderConnector = (
    leftMilestone: IntegrationMilestone,
    rightMilestone: IntegrationMilestone,
    index: number
  ) => {
    const leftSelected = selectedLabel === leftMilestone.label;
    const rightSelected = selectedLabel === rightMilestone.label;

    return (
      <div
        key={`connector-${leftMilestone.label}-${index}`}
        className={[
          "integration-timeline__connector",
          leftSelected ? "integration-timeline__connector--touch-right" : "",
          rightSelected ? "integration-timeline__connector--touch-left" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        aria-hidden
      >
        <span
          className={`integration-timeline__connector-line${
            isSegmentConnectorActive(leftMilestone)
              ? " integration-timeline__connector-line--active"
              : ""
          }`}
        />
      </div>
    );
  };

  return (
    <div
      className={[
        "integration-timeline",
        segmentedRail ? "integration-timeline--segmented-rail" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      role="list"
      style={
        {
          ["--integration-timeline-step-count" as string]: stepCount,
          ["--integration-timeline-progress" as string]: `${progressPercent}%`,
        } as React.CSSProperties
      }
    >
      {!segmentedRail && (
        <div className="integration-timeline__rail" aria-hidden>
          <span className="integration-timeline__rail-bg" />
          {progressPercent > 0 && (
            <span className="integration-timeline__rail-progress" />
          )}
        </div>
      )}
      {segmentedRail
        ? milestones.flatMap((milestone, index) => {
            const step = renderStep(milestone);
            if (index === milestones.length - 1) {
              return [step];
            }
            return [step, renderConnector(milestone, milestones[index + 1], index)];
          })
        : milestones.map((milestone) => renderStep(milestone))}
    </div>
  );
};

export default IntegrationTimeline;
